/**
 * Billing Hook
 *
 * Fetches and manages billing/subscription state including reverse trial status.
 * Uses React Query for caching and automatic refetching.
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/client/lib/api';
import { useAuth } from './use-auth';

/**
 * Reverse trial information from the API
 */
export interface ReverseTrial {
  isActive: boolean;
  daysRemaining: number | null;
  endsAt: string | null;
  startedAt: string | null;
  totalDays: number;
}

/**
 * Full billing response from /api/billing
 */
export interface BillingInfo {
  tier: 'free' | 'plus' | 'pro';
  effectiveTier: 'free' | 'plus' | 'pro';
  status: string | null;
  isPlusUser: boolean;
  isProUser: boolean;
  isTrialing: boolean;
  isTrialEligible: boolean;
  hasUsedTrial: boolean;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  // Reverse trial
  reverseTrial: ReverseTrial;
}

/**
 * Hook to fetch billing information including reverse trial status
 */
export function useBilling() {
  const { isAuthenticated } = useAuth();

  const {
    data: billing,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['billing'],
    queryFn: async (): Promise<BillingInfo> => {
      const response = await apiFetch('/api/billing', { skipWorkspaceHeader: true });
      if (!response.ok) {
        throw new Error('Failed to fetch billing info');
      }
      return response.json();
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  });

  return {
    billing,
    isLoading,
    error,
    refetch,

    // Convenience getters for reverse trial
    isInReverseTrial: billing?.reverseTrial?.isActive ?? false,
    reverseTrialDaysRemaining: billing?.reverseTrial?.daysRemaining ?? null,
    reverseTrialEndsAt: billing?.reverseTrial?.endsAt
      ? new Date(billing.reverseTrial.endsAt)
      : null,

    // Effective tier (accounts for reverse trial)
    effectiveTier: billing?.effectiveTier ?? 'free',

    // Legacy trial info
    isTrialing: billing?.isTrialing ?? false,
    isTrialEligible: billing?.isTrialEligible ?? false,
  };
}
